import React, { Component, ReactNode } from 'react';
import { Translation } from 'react-i18next';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertTriangle, Mail, MessageCircle, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { openInExternalBrowser } from '@/lib/platform';
import { consoleCapture } from '@/lib/consoleCapture';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDebugDetails: boolean;
  copied: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDebugDetails: false,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDebugDetails: false,
      copied: false,
    });
    // Reload the page to reset the app state
    window.location.href = '/';
  };

  handleContactSupport = () => {
    window.location.href = '/contact-us';
  };

  handleJoinDiscord = () => {
    openInExternalBrowser("https://discord.gg/FaYD2mJFhW");
  };

  toggleDebugDetails = () => {
    this.setState((prevState) => ({
      showDebugDetails: !prevState.showDebugDetails,
    }));
  };

  handleCopyLogs = async () => {
    const recentLogs = consoleCapture.getRecentLogs(50);
    const errorDetails = this.state.error 
      ? `\n=== ERROR DETAILS ===\n${this.state.error.toString()}\n${this.state.errorInfo?.componentStack || ''}\n\n`
      : '';
    const logsText = `=== GITFIT DEBUG LOGS ===\n${errorDetails}=== CONSOLE LOGS (Last 50) ===\n${consoleCapture.formatLogsAsText(recentLogs)}`;
    
    try {
      await navigator.clipboard.writeText(logsText);
      this.setState({ copied: true });
      setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy logs:', error);
      // Fallback for webview or restricted environments
      alert('Logs copied to clipboard!');
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Translation>
          {(t) => (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-lg w-full p-6">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertTriangle className="w-10 h-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold">{t('ui.text.oops_something_went_wrong')}</h1>
                <p className="text-muted-foreground">{t('ui.text.the_app_encountered_an_unexpected_error_this_somet')}</p>
              </div>

              <div className="w-full space-y-3">
                <p className="text-sm font-medium text-foreground">{t('ui.text.need_help')}</p>
                <div className="space-y-2">
                  <Button
                    onClick={this.handleContactSupport}
                    variant="outline"
                    className="w-full justify-start gap-3"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{t('ui.text.contact_support_via_email')}</span>
                  </Button>
                  <Button
                    onClick={this.handleJoinDiscord}
                    variant="outline"
                    className="w-full justify-start gap-3"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>{t('ui.text.join_our_discord_community')}</span>
                  </Button>
                </div>
              </div>

              <div className="w-full space-y-3">
                <Button
                  onClick={this.toggleDebugDetails}
                  variant="outline"
                  className="w-full justify-between"
                >
                  <span>{t('ui.text.see_debugging_details')}</span>
                  {this.state.showDebugDetails ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>

                {this.state.showDebugDetails && (
                  <div className="w-full space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{t('ui.text.recent_console_logs_last_50_entries')}</p>
                      <Button
                        onClick={this.handleCopyLogs}
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1"
                      >
                        {this.state.copied ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>{t('ui.text.copied')}</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>{t('ui.text.copy_logs')}</span>
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-md max-h-60 overflow-auto">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                        {consoleCapture.formatLogsAsText(consoleCapture.getRecentLogs(50))}
                      </pre>
                    </div>

                    {process.env.NODE_ENV === 'development' && this.state.error && (
                      <div className="bg-destructive/10 p-3 rounded-md">
                        <p className="text-xs font-semibold text-destructive mb-2">{t('ui.text.error_stack_dev_only')}</p>
                        <pre className="text-xs font-mono whitespace-pre-wrap break-all text-destructive">
                          {this.state.error.toString()}
                          {this.state.errorInfo?.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="w-full pt-2">
                <Button onClick={this.handleReset} className="w-full">{t('ui.text.return_to_home')}</Button>
              </div>
            </div>
          </Card>
        </div>
          )}
        </Translation>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
